// src/components/Onboarding/ImageUploadBox.js

import React from 'react';
import photoUploadIcon from '../../images/photo_upload.webp';
import uploadIcon from '../../images/upload_icon.svg';

/**
 * IMAGE UPLOAD BOX COMPONENT
 * 
 * Reusable component for image upload functionality in onboarding.
 * Features a centered layout with icon, instructions, and upload button.
 * Supports file upload with validation and error display.
 * 
 * PROPS:
 * - onUploadClick: Callback when upload button is clicked
 * - disabled: Whether upload button is disabled (default: false)
 * - theme: 'light' or 'dark' for theming (default: 'light')
 * - className: Additional CSS classes
 * - isUploading: Whether upload is in progress
 * - uploadProgress: Upload progress percentage (0-100)
 * - error: Error message to display
 * - uploadedFile: Successfully uploaded file data
 * 
 * USAGE:
 * <ImageUploadBox 
 *   onUploadClick={() => handleUpload()} 
 *   theme="light"
 *   isUploading={isUploading}
 *   error={error}
 * />
 */

export default function ImageUploadBox({
  onUploadClick,
  disabled = false,
  theme = 'light',
  className = '',
  isUploading = false,
  uploadProgress = 0,
  error = null,
  uploadedFile = null
}) {
  
  // Theme-aware styling
  const themeStyles = {
    light: {
      border: 'border-gray-300',
      background: 'bg-white',
      text: 'text-gray-700',
      button: {
        background: 'bg-mm_black hover:bg-gray-800',
        text: 'text-white',
        disabled: 'disabled:bg-gray-400'
      }
    },
    dark: {
      border: 'border-gray-600',
      background: 'bg-gray-900',
      text: 'text-gray-300',
      button: {
        background: 'bg-white hover:bg-gray-100',
        text: 'text-mm_black',
        disabled: 'disabled:bg-gray-600'
      }
    }
  };
  
  const currentTheme = themeStyles[theme];

  // Button text based on state
  const getButtonText = () => {
    if (isUploading) {
      return uploadProgress > 0 ? `Uploading... ${Math.round(uploadProgress)}%` : 'Uploading...';
    }
    if (uploadedFile) {
      return 'Photo uploaded!';
    }
    return 'Upload selfie';
  };

  // Button icon - show checkmark if uploaded successfully
  const getButtonIcon = () => {
    if (uploadedFile) {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      );
    }
    
    return (
      <img 
        src={uploadIcon}
        alt=""
        className="w-4 h-auto object-contain"
        aria-hidden="true"
      />
    );
  };
  
  return (
    <div className="w-full max-w-sm mx-auto">
      {/* Main Upload Box */}
      <div className={`
        w-full
        ${currentTheme.background}
        ${currentTheme.border}
        border rounded-2xl p-6 sm:p-8
        ${className}
      `}>
        {/* Upload Icon */}
        <div className="flex justify-center mb-5 sm:mb-6">
          <img 
            src={photoUploadIcon}
            alt="Photo upload"
            className="w-32 h-32 sm:w-20 sm:h-20 object-contain"
          />
        </div>
        
        {/* Instructions Text */}
        <div className="text-center mb-5 sm:mb-6">
          <p className={`text-sm ${currentTheme.text}`}>
            Face the camera<span className="mx-1">•</span>Portrait<span className="mx-1">•</span>Lighting
          </p>
        </div>
        
        {/* Upload Button */}
        <button
          onClick={onUploadClick}
          disabled={disabled || isUploading}
          className={`
            w-1/2 py-3 px-4 mx-auto
            ${uploadedFile 
              ? 'bg-green-600 hover:bg-green-700 text-white' // Success state
              : `${currentTheme.button.background} ${currentTheme.button.text}`
            }
            ${currentTheme.button.disabled} disabled:cursor-not-allowed
            text-sm font-medium
            rounded-lg
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
            flex items-center justify-center space-x-2
            ${isUploading ? 'opacity-75' : ''}
          `}
        >
          {/* Upload/Success Icon */}
          {getButtonIcon()}
          <span className="whitespace-nowrap leading-none text-[13px] sm:text-sm">{getButtonText()}</span>
        </button>

        {/* Upload Progress Bar */}
        {isUploading && uploadProgress > 0 && (
          <div className="mt-2 sm:mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${Math.round(uploadProgress)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-2 sm:mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {uploadedFile && (
        <div className="mt-2 sm:mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800">
                Photo uploaded successfully! ({(uploadedFile.fileSize / 1024 / 1024).toFixed(1)}MB)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
